import { ErrorState } from '@/components/primitives/ErrorState'
import { PageHeader } from '@/components/primitives/PageHeader'
import {
  type ImageModel,
  type ModelSettings,
  type TextModel,
  fetchModels,
  updateModels
} from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { Check, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

const card =
  'rounded-[36px] border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900'

/** $0.0013 → "$0.0013", $0.067 → "$0.067", $12.5 → "$12.50". */
function usd(n: number) {
  if (n === 0) return '$0'
  if (n < 0.01) return `$${n.toFixed(4).replace(/0+$/, '')}`
  if (n < 1) return `$${n.toFixed(3).replace(/0+$/, '')}`
  return `$${n.toFixed(2)}`
}

export function ModelsPage() {
  const query = useQuery({ queryKey: ['ai-models'], queryFn: fetchModels })
  const [saving, setSaving] = useState<string | null>(null)

  async function save(
    key: string,
    body: Parameters<typeof updateModels>[0],
    done: string
  ) {
    setSaving(key)
    try {
      const next = await updateModels(body)
      queryClient.setQueryData(['ai-models'], next)
      toast.success(done)
    } catch (err) {
      notifyError('Could not change the model', err)
    } finally {
      setSaving(null)
    }
  }

  const data = query.data

  return (
    <div className="mx-auto w-full max-w-6xl">
      <PageHeader
        title="Models & pricing"
        description="Pick what the AI team thinks and draws with. Prices are live from OpenRouter; every image still asks for your yes with its price."
      />

      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : !data ? (
        <Skeleton />
      ) : (
        <div className="space-y-10">
          <Summary data={data} />

          <section className="space-y-3">
            <SectionTitle
              title="Text model"
              hint={`What every agent thinks with. "Per reply" is a typical chat turn: about ${(data.turn.tokens_in / 1000).toFixed(0)}k tokens in, ${data.turn.tokens_out} out.`}
            />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.text.map((m) => (
                <TextCard
                  key={m.id}
                  model={m}
                  selected={
                    (data.text_model || data.default_text_model) === m.id
                  }
                  pending={saving === `text:${m.id}`}
                  disabled={!!saving}
                  onPick={() =>
                    save(
                      `text:${m.id}`,
                      { text_model: m.id },
                      `The team now thinks with ${m.name}.`
                    )
                  }
                />
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <SectionTitle
              title="Image model"
              hint="Makes ad and social images. OpenRouter has no Chinese image model yet, so these are Google and OpenAI."
            />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.image.map((m) => (
                <ImageCard
                  key={m.id}
                  model={m}
                  selected={
                    (data.image_model || data.default_image_model) === m.id
                  }
                  pending={saving === `image:${m.id}`}
                  disabled={!!saving}
                  onPick={() =>
                    save(
                      `image:${m.id}`,
                      { image_model: m.id },
                      `Images are now made with ${m.name}.`
                    )
                  }
                />
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <SectionTitle
              title="Per agent"
              hint="Give one agent a different model, e.g. a stronger one for Chief of Staff. Everyone else uses the team model."
            />
            <div
              className={cn(
                card,
                'divide-y divide-neutral-200 p-0 dark:divide-neutral-800'
              )}
            >
              {data.agents.map((a) => {
                const own = data.text.find((m) => m.id === a.model)
                return (
                  <div
                    key={a.key}
                    className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="font-semibold text-neutral-900 dark:text-white">
                        {a.name}
                      </p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {own
                          ? `${usd(own.per_turn)} per reply`
                          : 'Uses the team model'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {saving === `agent:${a.key}` && (
                        <Loader2 className="size-4 animate-spin text-neutral-400" />
                      )}
                      <select
                        value={a.model}
                        disabled={!!saving}
                        aria-label={`Model for ${a.name}`}
                        onChange={(e) =>
                          save(
                            `agent:${a.key}`,
                            { agents: { [a.key]: e.target.value } },
                            e.target.value
                              ? `${a.name} now uses ${data.text.find((m) => m.id === e.target.value)?.name}.`
                              : `${a.name} uses the team model again.`
                          )
                        }
                        className="w-full rounded-[36px] border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-400 disabled:opacity-50 sm:w-72 dark:border-neutral-800 dark:bg-neutral-950 dark:text-white"
                      >
                        <option value="">Team model</option>
                        {data.text.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name} · {usd(m.per_turn)}/reply
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

function Summary({ data }: { data: ModelSettings }) {
  const text = data.text.find(
    (m) => m.id === (data.text_model || data.default_text_model)
  )
  const image = data.image.find(
    (m) => m.id === (data.image_model || data.default_image_model)
  )
  const stats = [
    {
      label: 'Thinks with',
      value: text?.name ?? (data.text_model || data.default_text_model),
      sub: text ? `${usd(text.per_turn)} per reply` : 'Server default'
    },
    {
      label: 'Draws with',
      value: image?.name ?? (data.image_model || data.default_image_model),
      sub: image ? `≈${usd(image.per_image)} per image` : 'Server default'
    },
    {
      label: 'Last 7 days',
      value: usd(data.spend_7d),
      sub: `${data.runs_7d} agent runs`
    }
  ]
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {stats.map((s) => (
        <div key={s.label} className={card}>
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {s.label}
          </p>
          <p className="mt-1 truncate text-xl font-bold text-neutral-900 dark:text-white">
            {s.value}
          </p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {s.sub}
          </p>
        </div>
      ))}
    </div>
  )
}

function SectionTitle({ title, hint }: { title: string; hint: string }) {
  return (
    <div>
      <h2 className="text-lg font-bold text-neutral-900 dark:text-white">
        {title}
      </h2>
      <p className="text-sm text-neutral-500 dark:text-neutral-400">{hint}</p>
    </div>
  )
}

function Pick({
  selected,
  pending,
  disabled,
  onPick,
  children
}: {
  selected: boolean
  pending: boolean
  disabled: boolean
  onPick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled || selected}
      onClick={onPick}
      className={cn(
        'relative flex h-full flex-col gap-3 rounded-[36px] border bg-white p-5 text-left transition-all dark:bg-neutral-900',
        selected
          ? 'border-neutral-900 ring-1 ring-neutral-900 dark:border-white dark:ring-white'
          : 'border-neutral-200 hover:border-neutral-400 active:scale-[0.99] dark:border-neutral-800 dark:hover:border-neutral-600',
        disabled && !selected && 'opacity-60'
      )}
    >
      <span className="absolute right-5 top-5 flex size-6 items-center justify-center">
        {pending ? (
          <Loader2 className="size-4 animate-spin text-neutral-400" />
        ) : selected ? (
          <span className="flex size-6 items-center justify-center rounded-full bg-neutral-900 text-white dark:bg-white dark:text-black">
            <Check className="size-3.5" />
          </span>
        ) : null}
      </span>
      {children}
    </button>
  )
}

function Chip({
  tone = 'neutral',
  children
}: {
  tone?: 'neutral' | 'success'
  children: React.ReactNode
}) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[11px] font-semibold',
        tone === 'success'
          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
          : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
      )}
    >
      {children}
    </span>
  )
}

function TextCard({
  model: m,
  ...pick
}: {
  model: TextModel
  selected: boolean
  pending: boolean
  disabled: boolean
  onPick: () => void
}) {
  return (
    <Pick {...pick}>
      <div className="pr-8">
        <p className="font-bold text-neutral-900 dark:text-white">{m.name}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Chip>
            {m.maker} · {m.origin}
          </Chip>
          {m.tested && <Chip tone="success">Tested with the team</Chip>}
        </div>
      </div>
      <p className="text-sm text-neutral-600 dark:text-neutral-300">{m.note}</p>
      <div className="mt-auto">
        <p className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">
          {usd(m.per_turn)}
          <span className="ml-1 text-sm font-normal text-neutral-500 dark:text-neutral-400">
            per reply
          </span>
        </p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {usd(m.price_in)} in · {usd(m.price_out)} out per 1M tokens
          {!m.live && ' · last known price'}
        </p>
      </div>
    </Pick>
  )
}

function ImageCard({
  model: m,
  ...pick
}: {
  model: ImageModel
  selected: boolean
  pending: boolean
  disabled: boolean
  onPick: () => void
}) {
  return (
    <Pick {...pick}>
      <div className="pr-8">
        <p className="font-bold text-neutral-900 dark:text-white">{m.name}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <Chip>
            {m.maker} · {m.origin}
          </Chip>
        </div>
      </div>
      <p className="text-sm text-neutral-600 dark:text-neutral-300">{m.note}</p>
      <div className="mt-auto">
        <p className="text-2xl font-bold tabular-nums text-neutral-900 dark:text-white">
          ≈{usd(m.per_image)}
          <span className="ml-1 text-sm font-normal text-neutral-500 dark:text-neutral-400">
            per image
          </span>
        </p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Estimate for a ~1K image{!m.live && ' · last known price'}
        </p>
      </div>
    </Pick>
  )
}

function Skeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        {['a', 'b', 'c'].map((k) => (
          <div
            key={k}
            className="h-28 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800"
          />
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {['a', 'b', 'c', 'd', 'e', 'f'].map((k) => (
          <div
            key={k}
            className="h-48 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800"
          />
        ))}
      </div>
    </div>
  )
}
